import {Button} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import React, {useEffect} from 'react';
import {Link} from 'react-router-dom';

import {IconArrowRight} from '@tabler/icons-react';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {Subtitle} from 'src/design/atoms/Typography';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {SecondaryPanel} from 'src/design/atoms/SecondaryPanel';
import {FormViewDate} from 'src/app/forms/FormViewDate/FormViewDate';
import {FormViewRow} from 'src/app/forms/FormViewRow/FormViewRow';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useGetDemonstrationSummaryQuery} from 'src/redux/queries/courseSession';
import {SessionContentStatusIcon} from './sessionIcons';

export interface ViewLinkedDemonstrationSectionProps {
  taskAssignmentId: number;
}

export const ViewLinkedDemonstrationSection: React.FC<
  ViewLinkedDemonstrationSectionProps
> = ({taskAssignmentId}) => {
  const {setAlert} = useAlert();

  const {
    data: blend,
    isLoading,
    error,
  } = useGetDemonstrationSummaryQuery(
    {taskAssignmentId},
    {skip: !taskAssignmentId}
  );

  useEffect(() => {
    if (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  }, [error]);

  if (isLoading) {
    return (
      <SecondaryPanel>
        <Subtitle text="Related Training" />
        <TableBodyRender isLoading={true} />
      </SecondaryPanel>
    );
  }

  if (!blend) {
    return (
      <SecondaryPanel>
        <Subtitle text="Related Training" />
        <Alert color="red">
          Expected training session data not found for this task assignment or
          task definition.
        </Alert>
      </SecondaryPanel>
    );
  }

  return (
    <SecondaryPanel>
      <Stack>
        <Subtitle text="Related Training" />
        <Group justify="space-between" align="flex-end">
          <FormViewRow
            label="Session ID"
            value={String(blend?.session?.internalIdentifier ?? '')}
          />
          <PermissionGuard
            module={ModuleEnum.Training}
            operation={PermissionOperationEnum.View}>
            <Box>
              <Button
                component={Link}
                to={PathUtility.TrainingCourseSessionView(
                  String(blend?.definition?.id),
                  String(blend?.assignment?.id),
                  String(blend?.session?.id),
                  'overview'
                )}
                size="compact-sm"
                variant="subtle"
                color="black"
                rightSection={<IconArrowRight />}>
                View Session
              </Button>
            </Box>
          </PermissionGuard>
        </Group>

        <FormViewRow
          label="Trainee"
          value={blend.assignment?.assignedTo?.name}
        />
        <SimpleGrid cols={2}>
          <FormViewDate label="Started At" value={blend.session.startedAt} />
          <FormViewDate
            label="Completed At"
            value={blend.session.completedAt}
          />
        </SimpleGrid>

        {blend.session.contents?.map((sContent, index) => {
          const dContent = blend.definition.contents?.filter(
            i => i.id == sContent.contentDefinitionId
          )[0];

          if (dContent === undefined) {
            return <p>Invalid definition for session content {sContent.id}</p>;
          }

          const title =
            dContent.quiz?.headerText ??
            dContent.lesson?.headerText ??
            dContent.demonstration?.headerText;

          return (
            <Stack key={sContent.id} gap="sm">
              <Group gap="sm">
                <SessionContentStatusIcon state={sContent.state} />

                <Title size="sm">
                  {index + 1}. {title}
                </Title>
              </Group>
              <Group justify="space-between">
                <Text size="xs" c="dimmed">
                  Started:{' '}
                  {sContent.startedAt?.toString().toLongDate() ?? 'N/A'}
                </Text>
                <Text size="xs" c="dimmed">
                  Completed:{' '}
                  {sContent.completedAt?.toString().toLongDate() ?? 'N/A'}
                </Text>
              </Group>
            </Stack>
          );
        })}
      </Stack>
    </SecondaryPanel>
  );
};
